/** @jsxImportSource @emotion/react */
'use memo'

import css from '@emotion/css/macro'
import type { ReactNode } from 'react'
import colors from '@sportninja/common/constants/appColors'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import Text from 'src/components/ts-components/Text'

interface SettingsSectionProps {
  title: string
  description: string
  children: ReactNode
}

/** One part of a settings group in its own darker panel, so the parts read apart. */
export const SettingsSection = ({
  title,
  description,
  children,
}: SettingsSectionProps) => (
  <section css={sectionStyle} aria-label={capitalizeWords(title)}>
    <div css={headingStyle}>
      <Text variant='paragraphSmall' weight='semiBold' color={colors.WHITE}>
        {capitalizeWords(title)}
      </Text>
      <Text variant='paragraphXSmall' color={colors.NEUTRAL_50}>
        {description}
      </Text>
    </div>
    {children}
  </section>
)

// The dev-server type check doesn't see SECONDARY_350 on `colors`, though it is defined.
const palette = colors as Record<string, string>

const sectionStyle = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
  border-radius: 8px;
  background-color: ${palette.SECONDARY_350};
`

const headingStyle = css`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-bottom: 12px;
  border-bottom: 1px solid ${colors.SOFT_STEEL};
`
